import type { AsyncSequence } from "#public"

/**
 * Reduces an async sequence to a single value using an accumulator function.
 * Processes items sequentially, updating the accumulator with each item.
 *
 * @param func - Reducer function that takes accumulator, current item, and index
 * @param sequence - The async sequence to reduce
 * @param initial - Initial value for the accumulator
 * @returns A promise that resolves to the final accumulated value
 *
 * @example
 * ```ts
 * import { ReduceAsync } from "@pdfking/tfunc"
 *
 * async function* numbers() {
 *   yield 1;
 *   yield 2;
 *   yield 3;
 *   yield 4;
 * }
 *
 * const sum = await ReduceAsync(
 *   (acc, value) => acc + value,
 *   numbers(),
 *   0
 * );
 * console.log(sum); // 10
 * ```
 */
export const ReduceAsync = async <T, A>(
  func: (accumulator: A, item: T, index: number) => A | Promise<A>,
  sequence: AsyncSequence<T>,
  initial: A
): Promise<A> => {
  let accumulator = initial
  let index = 0

  for await (const item of sequence) {
    accumulator = await func(accumulator, item, index++)
  }

  return accumulator
}
